<mat-card>
  <mat-card-header>
    <mat-card-title>
      <mat-icon class="title-icon">add_circle</mat-icon>
      Add Roles or Access
    </mat-card-title>
  </mat-card-header>
  <mat-card-content>
    @if (isLoading()) {
      <div class="loading-container">
        <mat-progress-spinner mode="indeterminate" diameter="50"></mat-progress-spinner>
        <p>Adding roles...</p>
      </div>
    } @else {
      <!-- Role Type Selection -->
      <div class="role-type-section">
        <h3>Select Type</h3>
        <mat-radio-group [ngModel]="selectedRoleType()" (ngModelChange)="onRoleTypeChange($event)" data-testid="role-type-selector">
          <mat-radio-button value="game" data-testid="game-role-option">
            <mat-icon>videogame_asset</mat-icon>
            Game-Specific Role
          </mat-radio-button>
          <mat-radio-button value="cross-game" data-testid="cross-game-role-option">
            <mat-icon>public</mat-icon>
            Cross-Game Role (All Games)
          </mat-radio-button>
          <mat-radio-button value="charity" data-testid="charity-admin-option">
            <mat-icon>volunteer_activism</mat-icon>
            Charity Admin Access
          </mat-radio-button>
        </mat-radio-group>
      </div>

      <!-- Game Selection (shown when game or charity type is selected) -->
      @if (needsGameSelectionBoolean()) {
        <div class="game-selection-section">
          <h3>Select Game</h3>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Select Game</mat-label>
            <mat-select [ngModel]="selectedGame()" (ngModelChange)="onGameChange($event)" data-testid="game-select">
              @for (game of availableGames; track game.Id) {
                <mat-option [value]="game">{{ game.Name }}</mat-option>
              }
            </mat-select>
            <mat-hint>Choose the game for this role assignment</mat-hint>
          </mat-form-field>
        </div>
      }

      <!-- Game Roles Selection -->
      @if (selectedRoleType() === 'game' && selectedGame()) {
        <div class="selection-section">
          <h3>Select Game Roles for {{ selectedGame()!.Name }}</h3>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Select Roles</mat-label>
            <mat-select [ngModel]="selectedRoles()" (ngModelChange)="selectedRoles.set($event)" multiple data-testid="game-roles-select">
              @for (role of availableGameRoles; track role.Id) {
                <mat-option [value]="role">
                  {{ role.Name }}
                </mat-option>
              }
            </mat-select>
            <mat-hint>Select one or more game-specific roles</mat-hint>
          </mat-form-field>

          @if (selectedRoles().length > 0) {
            <div class="selected-items">
              <strong>Selected Roles:</strong>
              <mat-chip-set>
                @for (role of selectedRoles(); track role.Id) {
                  <mat-chip [removable]="true" (removed)="removeRole(role)" data-testid="selected-game-role-chip">
                    {{ role.Name }}
                    <button matChipRemove>
                      <mat-icon>cancel</mat-icon>
                    </button>
                  </mat-chip>
                }
              </mat-chip-set>
            </div>
          }
        </div>
      }

      <!-- Cross-Game Roles Selection -->
      @if (selectedRoleType() === 'cross-game') {
        <div class="selection-section">
          <h3>Select Cross-Game Roles</h3>
          @if (availableCrossGameRoles().length === 0) {
            <p class="no-roles-message">
              <mat-icon>info</mat-icon>
              This user already has all available cross-game roles.
            </p>
          } @else {
            <mat-form-field appearance="outline" class="full-width">
              <mat-label>Select Roles</mat-label>
              <mat-select [ngModel]="selectedRoles()" (ngModelChange)="selectedRoles.set($event)" multiple data-testid="cross-game-roles-select">
                @for (role of availableCrossGameRoles(); track role.Id) {
                  <mat-option [value]="role">
                    {{ role.Name }}
                  </mat-option>
                }
              </mat-select>
              <mat-hint>Select one or more cross-game roles</mat-hint>
            </mat-form-field>

            @if (selectedRoles().length > 0) {
              <div class="selected-items">
                <strong>Selected Roles:</strong>
                <mat-chip-set>
                  @for (role of selectedRoles(); track role.Id) {
                    <mat-chip [removable]="true" (removed)="removeRole(role)" data-testid="selected-cross-game-role-chip">
                      {{ role.Name }}
                      <button matChipRemove>
                        <mat-icon>cancel</mat-icon>
                      </button>
                    </mat-chip>
                  }
                </mat-chip-set>
              </div>
            }
          }
        </div>
      }

      <!-- Charity Admin Selection -->
      @if (selectedRoleType() === 'charity' && selectedGame()) {
        <div class="selection-section">
          <h3>Select Charities for {{ selectedGame()!.Name }}</h3>
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Select Charities</mat-label>
            <mat-select [ngModel]="selectedCharities()" (ngModelChange)="selectedCharities.set($event)" multiple data-testid="charities-select">
              @for (charity of availableCharities; track charity.Id) {
                <mat-option [value]="charity">
                  {{ charity.Name }}
                </mat-option>
              }
            </mat-select>
            <mat-hint>Select one or more charities to grant admin access</mat-hint>
          </mat-form-field>

          @if (selectedCharities().length > 0) {
            <div class="selected-items">
              <strong>Selected Charities:</strong>
              <mat-chip-set>
                @for (charity of selectedCharities(); track charity.Id) {
                  <mat-chip [removable]="true" (removed)="removeCharity(charity)" data-testid="selected-charity-chip">
                    {{ charity.Name }}
                    <button matChipRemove>
                      <mat-icon>cancel</mat-icon>
                    </button>
                  </mat-chip>
                }
              </mat-chip-set>
            </div>
          }
        </div>
      }
    }
  </mat-card-content>

  <mat-card-actions>
    <button mat-button (click)="onCancel()" [disabled]="isLoading()" data-testid="cancel-add-roles-button">
      Cancel
    </button>
    <button mat-raised-button color="primary"
            (click)="onSave()"
            [disabled]="!canSaveRoles()"
            data-testid="save-new-roles-button">
      <mat-icon>save</mat-icon>
      Add Selected
    </button>
  </mat-card-actions>
</mat-card>
